import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import {
	getMauiIdFromAdfNode,
	getMauiIdFromUrl,
} from '@atlassian/malleable-ui-components/editor-remix/remix-ids';

import type { RemixResultDeletionAttributes } from './remix-result-tracker';

type DeletedResult = {
	infographicType?: string;
	localId?: string;
	resultId: string;
	resultType: 'media' | 'maui';
};

type ResultDeletedEvent = {
	action: 'deleted';
	actionSubject: 'remixResult';
	attributes: Partial<RemixResultDeletionAttributes> & {
		infographicType?: string;
		localId?: string;
		pageId: string;
		resultId: string;
		resultType?: DeletedResult['resultType'];
	};
	eventType: 'track';
};

const getResult = (node: Node, parent: Node | null): DeletedResult | undefined => {
	const localId = typeof node.attrs.localId === 'string' ? node.attrs.localId : undefined;
	if (node.type.name === 'media') {
		const infographicType: unknown = node.marks.find((mark) => mark.type.name === 'dataConsumer')
			?.attrs.sources?.[0];
		if (typeof node.attrs.id !== 'string' || !node.attrs.id) {
			return;
		}
		return {
			resultType: 'media',
			infographicType:
				typeof infographicType === 'string' && infographicType.startsWith('remix:')
					? infographicType
					: undefined,
			localId:
				parent?.type.name === 'mediaSingle' &&
				typeof parent.attrs.localId === 'string' &&
				parent.attrs.localId
					? parent.attrs.localId
					: localId,
			resultId: node.attrs.id,
		};
	}
	const resultId =
		node.type.name === 'embedCard' ? getMauiIdFromUrl(node.attrs.url) : getMauiIdFromAdfNode(node);
	return resultId ? { resultType: 'maui', localId, resultId } : undefined;
};

const getKey = (result: DeletedResult): string =>
	JSON.stringify([result.resultType, result.localId || result.resultId]);

const getResultKey = (result: DeletedResult): string =>
	JSON.stringify([result.resultType, result.resultId]);

const collectResults = (
	doc: Node,
	keys: Set<string>,
	resultCounts: Map<string, number>,
): Map<string, DeletedResult[]> => {
	const results = new Map<string, DeletedResult[]>();
	doc.descendants((node, _pos, parent) => {
		const result = getResult(node, parent);
		if (result) {
			const resultKey = getResultKey(result);
			const count = resultCounts.get(resultKey);
			if (count !== undefined) {
				resultCounts.set(resultKey, count + 1);
			}
			const key = getKey(result);
			if (keys.has(key)) {
				const matches = results.get(key) ?? [];
				matches.push(result);
				results.set(key, matches);
			}
		}
	});
	return results;
};

export const createRemixResultDeletionAnalyticsPlugin = ({
	analytics,
	getPageId,
	getDeletionAttributes,
	isPendingReplacement,
	isTrackedReplacement,
}: {
	analytics: EditorAnalyticsAPI | undefined;
	getDeletionAttributes?: (
		result: DeletedResult,
		pageId: string,
	) => RemixResultDeletionAttributes | undefined;
	getPageId: () => string | undefined;
	isPendingReplacement?: (result: Pick<DeletedResult, 'localId' | 'resultId'>) => boolean;
	isTrackedReplacement?: (result: DeletedResult, oldDoc: Node, newDoc: Node) => boolean;
}): SafePlugin =>
	new SafePlugin({
		appendTransaction(transactions, oldState, newState) {
			if (!isExperimentEnabled('platform_maui_retention_analytics')) {
				return null;
			}
			const pageId = getPageId();
			if (
				!analytics ||
				!pageId ||
				oldState.doc === newState.doc ||
				transactions.some((tr) => tr.getMeta('isRemote') || tr.getMeta('replaceDocument'))
			) {
				return null;
			}
			const start = oldState.doc.content.findDiffStart(newState.doc.content);
			const end = oldState.doc.content.findDiffEnd(newState.doc.content);
			if (start === null || !end) {
				return null;
			}
			const keys = new Set<string>();
			const previousCounts = new Map<string, number>();
			const overlap = Math.max(0, start - Math.min(end.a, end.b));
			oldState.doc.nodesBetween(start, end.a + overlap, (node, _pos, parent) => {
				const result = getResult(node, parent);
				if (result) {
					if (
						result.resultType === 'media' &&
						!result.infographicType &&
						!getDeletionAttributes?.(result, pageId)
					) {
						return;
					}
					keys.add(getKey(result));
					previousCounts.set(getResultKey(result), 0);
				}
			});
			if (keys.size === 0) {
				return null;
			}
			const currentCounts = new Map(previousCounts);
			const previous = collectResults(oldState.doc, keys, previousCounts);
			const current = collectResults(newState.doc, keys, currentCounts);
			for (const [key, results] of previous) {
				const remaining = [...(current.get(key) ?? [])];
				const removed = results.filter((result) => {
					const index = remaining.findIndex((match) => match.resultId === result.resultId);
					if (index < 0) {
						return true;
					}
					remaining.splice(index, 1);
					return false;
				});
				// A different result at the same localId is a replacement, not a deletion.
				for (const result of removed.slice(remaining.length)) {
					const resultKey = getResultKey(result);
					const previousCount = previousCounts.get(resultKey) ?? 0;
					if (previousCount <= (currentCounts.get(resultKey) ?? 0)) {
						continue;
					}
					previousCounts.set(resultKey, previousCount - 1);
					if (
						isPendingReplacement?.(result) ||
						isTrackedReplacement?.(result, oldState.doc, newState.doc)
					) {
						continue;
					}
					const sessionAttributes = getDeletionAttributes?.(result, pageId);
					// Without a dataConsumer remix source, CWR images are indistinguishable from ordinary uploads.
					// Track their deletions only with session attribution. Marked Remix images work across sessions.
					if (result.resultType === 'media' && !result.infographicType && !sessionAttributes) {
						continue;
					}
					analytics.fireAnalyticsEvent<ResultDeletedEvent, 'customEventType'>({
						action: 'deleted',
						actionSubject: 'remixResult',
						eventType: 'track',
						attributes: {
							...(sessionAttributes ?? { resultType: result.resultType }),
							pageId,
							resultId: result.resultId,
							...(result.localId ? { localId: result.localId } : {}),
							...(result.infographicType ? { infographicType: result.infographicType } : {}),
						},
					});
				}
			}
			return null;
		},
	});
